import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UsersList } from "../UsersList/UsersList";
import { ReactComponent as TickIcon } from "../../images/TickIcon.svg";
import NewCheckBox from "components-new/NewCheckBox";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { changeActiveFilter } from "toolkit/users/users.thunks";
import cn from "classnames";
import styles from "./UsersTable.module.sass";
import { usePagination } from "hooks/usePagination";
import { setUsers } from "toolkit/users/users.slice";
import {userAPI} from "../../libs/apiClient/User";

interface UsersTableProps {
	archive: boolean;
	select: boolean;
	query: string | number;
	org: any;
	outline: (id: number) => string;
}

const FilterColumn: React.FC<any> = ({ title, radios = [], params, archive, setParams, fetching }) => {
	const ref = useRef<HTMLTableCellElement | null>(null);

	const dispatch = useAppDispatch();

	const [open, setOpen] = useState<boolean>(false);
	useOutsideAlerter(ref.current, () => setOpen(false));

	return (
		<div
			className={cn(styles.dropDown, { [styles.active]: open })}
			ref={ref}
			onClick={(e) => {
				e.stopPropagation();
				setOpen(true);
			}}>
			<div className={styles.dropDownText}>
				<span>{title}</span>
				<TickIcon className={cn(styles.dropDownIcon, { [styles.active]: open })} />
			</div>

			{open && (
				<div className={styles.dropDownPopup}>
					{radios.map((_: any, index: number) => {
						return (
							<label key={index} className={styles.dropDownItem}>
								<NewCheckBox
									name={_.name}
									checked={_.active}
									onChange={() => {
										dispatch(
											changeActiveFilter({
												params: { ...params, page: 1, filter_active: archive },
												item: _,
												setParams,
												fetching,
											}),
										);
										fetching();
									}}
									type={"radio"}
								/>
								{_.name}
							</label>
						);
					})}
				</div>
			)}
		</div>
	);
};

export const UsersTable: FC<UsersTableProps> = ({ archive, select, query, outline }) => {
	const dispatch = useAppDispatch();

	/**
	 * здесь изначально устанавливается первая страница и количество выводимых элементов
	 */
	const [params, setParams] = useState<any>({
		page: 1,
		num: 30,
	});

	/**
	 * если активная, список сотрудников переписывается, а если не активная, дописывает значения в массив пользователей
	 */
	const [changingData, setChangingData] = useState(true);

	/**
	 * нужен для первой загрузки для фильтров, работает вместе с changingData, если они оба активные то фильтры создаются
	 */
	const [tempFilter, setTempFilter] = useState(true);

	const { status, position, role } = useAppSelector((state) => state.users.usersFilters);

	/**
	 * Здесь создается базовый URL для запроса
	 */
	const linkUsers = useMemo(() => {
		let link = `/users/long`;

		if (query) {
			link += `?query=${query}`;
		}

		if (select) {
			link += "/unbound";
		}
		return link;
	}, [select, query]);

	/**
	 * хранит прокручиваемый контейнер, для подгрузки данных
	 */
	const scrollData = useRef<HTMLDivElement | null>(null);

	/**
	 * функция обработки ответа от сервера
	 */
	const then = useCallback(
		(data: any) => {
			if (data.list.length < params.num || !data.list.length) {
				/* если ответа нет или он не полный то устанавливается флаг прекращения запросов */
				setIsFinally(true);
			} else {
				/* иначе устанавливается следующая страница для запроса */
				setParams((prevState: any) => ({ ...prevState, page: prevState.page + 1 }));
			}

			dispatch(setUsers({ data, change: changingData, filterData: tempFilter }));

			setTempFilter(false);

			/* если запрос прошел с полным ответом, переключилась страница, то выключается флаг для перезаписи пользователей */
			if (changingData || params.num !== 1) {
				setChangingData(false);
			}

			//eslint-disable-next-line
		}, [changingData, tempFilter, params],
	);

	const { setIsFinally, setFetch, fetch } = usePagination<any>(
		linkUsers,
		{
			...params,
			filter_active: !archive,
		},
		then,
		scrollData,
	);

	useEffect(() => {
		if (!fetch) {
			setParams((prevState: any) => ({ ...prevState, page: 1 }));
			setChangingData(true);
			setFetch(true);
			setIsFinally(false);
		}
		//eslint-disable-next-line
	}, [archive, select, query]);
	const [readCard, setReadCard] = useState(false);
	useEffect(() => {
		userAPI.permissionsCheckCreate(0, ["system.user.read"]).then(({ data }) => {
			setReadCard(!!data.length);
		});
	}, []);
	return (
		<div className={styles.head}>
			<div className={styles.row}>
				<div className={styles.item}>ID</div>
				<div className={styles.item}>ФИО пользователя</div>
				<FilterColumn
					archive={archive}
					title="Роль в организации"
					radios={role}
					params={params}
					setParams={(params: any) => {
						setParams(params);
						setChangingData(true);
					}}
					fetching={() => {
						setIsFinally(false);
						setFetch(true);
					}}
				/>
				<div className={styles.item}>Почта</div>
				<div className={styles.item}>Табельный номер</div>
				<FilterColumn
					archive={archive}
					title="Должность"
					radios={position}
					params={params}
					setParams={(params: any) => {
						setParams(params);
						setChangingData(true);
					}}
					fetching={() => {
						setIsFinally(false);
						setFetch(true);
					}}
				/>
				<FilterColumn
					archive={archive}
					title="Статус"
					radios={status}
					params={params}
					setParams={(params: any) => {
						setParams(params);
						setChangingData(true);
					}}
					fetching={() => {
						setIsFinally(false);
						setFetch(true);
					}}
				/>
				<div className={styles.item}>Компания-работодатель</div>
			</div>

			<UsersList
				read={readCard}
				outline={outline}
				ref={scrollData}
				searchValue={query}
				archive={archive}
				select={select}
			/>
		</div>
	);
};
